import { isEmpty, isNotEmpty } from '@/shared/util';

import { createQuillListItem } from './builder.util';
import { MAX_QUILL_LIST_INDENT } from './list.constant';
import {
  WORD_ONLINE_LIST_CONTAINER_SELECTOR,
  WORD_ONLINE_LIST_ITEM_SELECTOR,
  WORD_ONLINE_LIST_LEVEL_PATTERN,
} from './nested-list-clipboard.constant';
import { QuillListType, WordOnlineListBlock } from './nested-list-clipboard.model';
import { areNearbyListBlocks } from './nested-list-clipboard-dom.util';

export function hasWordOnlineListItems(html: string): boolean {
  return WORD_ONLINE_LIST_LEVEL_PATTERN.test(html);
}

function getOutermostList(listItem: HTMLLIElement): HTMLElement | null {
  let outermostList: HTMLElement | null = null;
  let list = listItem.closest('ul, ol');

  while (list instanceof HTMLElement) {
    outermostList = list;
    list = list.parentElement?.closest('ul, ol') ?? null;
  }

  return outermostList;
}

function getWordOnlineBlockElement(listItem: HTMLLIElement): HTMLElement | null {
  const container = listItem.closest(WORD_ONLINE_LIST_CONTAINER_SELECTOR);

  if (container instanceof HTMLElement) {
    return container;
  }

  return getOutermostList(listItem);
}

function findWordOnlineListBlocks(root: ParentNode): WordOnlineListBlock[] {
  const blocksByElement = new Map<HTMLElement, WordOnlineListBlock>();

  for (const listItem of Array.from(root.querySelectorAll(WORD_ONLINE_LIST_ITEM_SELECTOR))) {
    if (!(listItem instanceof HTMLLIElement)) {
      continue;
    }

    const element = getWordOnlineBlockElement(listItem);

    if (!element) {
      continue;
    }

    const block = blocksByElement.get(element);

    if (block) {
      block.listItems.push(listItem);
      continue;
    }

    blocksByElement.set(element, { element, listItems: [listItem] });
  }

  return Array.from(blocksByElement.values());
}

function groupConsecutiveWordOnlineBlocks(blocks: WordOnlineListBlock[]): WordOnlineListBlock[][] {
  if (isEmpty(blocks)) {
    return [];
  }

  const groups: WordOnlineListBlock[][] = [[blocks[0]]];

  for (let index = 1; index < blocks.length; index += 1) {
    const previousBlock = blocks[index - 1];
    const currentBlock = blocks[index];

    if (areNearbyListBlocks(previousBlock.element, currentBlock.element)) {
      groups[groups.length - 1].push(currentBlock);
      continue;
    }

    groups.push([currentBlock]);
  }

  return groups;
}

function getWordOnlineListIndent(listItem: HTMLLIElement): number {
  const level = Number.parseInt(listItem.dataset.ariaLevel ?? '', 10);

  if (Number.isNaN(level)) {
    return 0;
  }

  return Math.min(Math.max(level - 1, 0), MAX_QUILL_LIST_INDENT);
}

function getWordOnlineListType(listItem: HTMLLIElement): QuillListType {
  return listItem.parentElement?.tagName === 'OL' ? 'ordered' : 'bullet';
}

function cloneWordOnlineListItemContent(listItem: HTMLLIElement): DocumentFragment {
  const fragment = document.createDocumentFragment();

  for (const child of Array.from(listItem.childNodes)) {
    if (child instanceof HTMLUListElement || child instanceof HTMLOListElement) {
      continue;
    }

    if (child instanceof HTMLParagraphElement) {
      for (const paragraphChild of Array.from(child.childNodes)) {
        fragment.appendChild(paragraphChild.cloneNode(true));
      }

      continue;
    }

    fragment.appendChild(child.cloneNode(true));
  }

  return fragment;
}

function hasWordOnlineListItemContent(listItem: HTMLLIElement): boolean {
  const container = document.createElement('div');

  container.appendChild(cloneWordOnlineListItemContent(listItem));

  return isNotEmpty(container.textContent) || Boolean(container.querySelector('img'));
}

function convertWordOnlineBlockGroup(group: WordOnlineListBlock[]): HTMLOListElement {
  const flattenedList = document.createElement('ol');

  for (const listItem of group.flatMap((block) => block.listItems)) {
    if (!hasWordOnlineListItemContent(listItem)) {
      continue;
    }

    flattenedList.appendChild(
      createQuillListItem({
        content: cloneWordOnlineListItemContent(listItem),
        indent: getWordOnlineListIndent(listItem),
        listType: getWordOnlineListType(listItem),
      }),
    );
  }

  return flattenedList;
}

export function convertWordOnlineListItems(root: ParentNode): boolean {
  const blocks = findWordOnlineListBlocks(root);

  if (isEmpty(blocks)) {
    return false;
  }

  let didConvert = false;

  for (const group of groupConsecutiveWordOnlineBlocks(blocks)) {
    const flattenedList = convertWordOnlineBlockGroup(group);
    const firstBlock = group[0]?.element;

    if (!firstBlock?.parentElement || isEmpty(Array.from(flattenedList.children))) {
      continue;
    }

    firstBlock.parentElement.insertBefore(flattenedList, firstBlock);

    for (const block of group) {
      block.element.remove();
    }

    didConvert = true;
  }

  return didConvert;
}
